// ProductSpecifications.tsx
import { useState, useEffect } from 'react';
import { Row, Col, Form, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt, faPlus } from '@fortawesome/free-solid-svg-icons';
import { ProductSpc } from 'api/products/_requests';

interface ProductSpecificationsProps {
  specification: ProductSpc[];
  setSpecification: React.Dispatch<React.SetStateAction<ProductSpc[]>>;
}

const ProductSpecifications = ({
  specification,
  setSpecification
}: ProductSpecificationsProps) => {
  const [errors, setErrors] = useState<Array<Partial<ProductSpc>>>(() =>
    specification.map(() => ({}))
  );

  /* keep error length in sync */
  useEffect(() => {
    setErrors(prev => specification.map((_, i) => prev[i] ?? {}));
  }, [specification.length]);

  const validateRow = (i: number) => {
    const { header, value } = specification[i] ?? {};
    const rowErr: Partial<ProductSpc> = {};
    if (header && !value) rowErr.value = 'Value required';
    if (value && !header) rowErr.header = 'Header required';
    setErrors(prev => {
      const copy = [...prev];
      copy[i] = rowErr;
      return copy;
    });
  };

  const updateSpec = (i: number, field: keyof ProductSpc, val: string) => {
    setSpecification(p => {
      const copy = [...p];
      copy[i] = { ...copy[i], [field]: val };
      return copy;
    });
    validateRow(i);
  };

  const addSpec = () =>
    setSpecification(p => [...p, { header: '', value: '' }]);

  const removeSpec = (i: number) =>
    setSpecification(p => p.filter((_, idx) => idx !== i));

  return (
    <>
      {specification.map((spec, i) => (
        <Row key={i} className="g-2 mb-2 align-items-start">
          {/* Header */}
          <Col xs={12} md={5}>
            <Form.Control
              value={spec.header}
              placeholder="Header"
              isInvalid={!!errors[i]?.header}
              onChange={e => updateSpec(i, 'header', e.target.value)}
            />
            <Form.Control.Feedback type="invalid">
              {errors[i]?.header}
            </Form.Control.Feedback>
          </Col>

          <Col xs={12} md={5}>
            <Form.Control
              value={spec.value}
              placeholder="Value"
              isInvalid={!!errors[i]?.value}
              onChange={e => updateSpec(i, 'value', e.target.value)}
            />
            <Form.Control.Feedback type="invalid">
              {errors[i]?.value}
            </Form.Control.Feedback>
          </Col>

          <Col xs={12} md={2} className="text-md-end">
            <Button
              variant="outline-danger"
              type="button"
              className="w-100 w-md-auto"
              onClick={() => removeSpec(i)}
            >
              <FontAwesomeIcon icon={faTrashAlt} />
            </Button>
          </Col>
        </Row>
      ))}

      <Button variant="outline-primary" type="button" onClick={addSpec}>
        <FontAwesomeIcon icon={faPlus} className="me-1" />
        Add Specification
      </Button>
    </>
  );
};

export default ProductSpecifications;
